export const STORAGE_KEYS = {
  accessToken: "access_token",
  refreshToken: "refresh_token",
  customer: "customer",
  guestCart: "guest_cart",
  selectedLocation: "mdmart_selected_location",
};

let accessTokenMemory = null;
let refreshTokenMemory = null;
let authSessionVersion = 0;

const canUseBrowserStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const storageGet = (key) => {
  if (!canUseBrowserStorage()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (key, value) => {
  if (!canUseBrowserStorage() || value === undefined || value === null) return;

  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Storage can be blocked in private browsing; keep the in-memory session.
  }
};

const storageRemove = (key) => {
  if (!canUseBrowserStorage()) return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
};

export const dispatchAuthChanged = () => {
  authSessionVersion += 1;

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("authChanged"));
  }
};

export const dispatchCartChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("cartUpdated"));
  }
};

export const getAccessToken = () =>
  accessTokenMemory || storageGet(STORAGE_KEYS.accessToken);

export const getRefreshToken = () =>
  refreshTokenMemory || storageGet(STORAGE_KEYS.refreshToken);

export const isAuthenticated = () => Boolean(getAccessToken());

export const getAuthSessionVersion = () => authSessionVersion;

export const getAuthSnapshot = () =>
  `${authSessionVersion}:${isAuthenticated() ? "authenticated" : "guest"}`;

export const subscribeToAuthChanges = (listener) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener("authChanged", listener);
  return () => window.removeEventListener("authChanged", listener);
};

export const setAccessToken = (token) => {
  accessTokenMemory = token || null;

  if (token) {
    storageSet(STORAGE_KEYS.accessToken, token);
  } else {
    storageRemove(STORAGE_KEYS.accessToken);
  }
};

export const setAuthSession = ({
  access,
  refresh,
  customer,
  persistRefreshToken = true,
} = {}) => {
  if (access) {
    setAccessToken(access);
  }

  if (refresh) {
    refreshTokenMemory = refresh;

    // Current backend still returns refresh tokens to the frontend. Keep this
    // compatibility fallback centralized so an httpOnly-cookie backend can
    // remove localStorage refresh tokens without changing every component.
    if (persistRefreshToken) {
      storageSet(STORAGE_KEYS.refreshToken, refresh);
    } else {
      storageRemove(STORAGE_KEYS.refreshToken);
    }
  }

  if (customer) {
    storageSet(STORAGE_KEYS.customer, JSON.stringify(customer));
  }

  dispatchAuthChanged();
};

export const getStoredCustomer = () => {
  const raw = storageGet(STORAGE_KEYS.customer);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearAuthSession = ({ keepGuestCart = true } = {}) => {
  accessTokenMemory = null;
  refreshTokenMemory = null;

  storageRemove(STORAGE_KEYS.accessToken);
  storageRemove(STORAGE_KEYS.refreshToken);
  storageRemove(STORAGE_KEYS.customer);

  if (!keepGuestCart) {
    storageRemove(STORAGE_KEYS.guestCart);
  }

  dispatchAuthChanged();
  dispatchCartChanged();
};

export const getSelectedLocation = () =>
  storageGet(STORAGE_KEYS.selectedLocation);

export const setSelectedLocation = (value) => {
  if (value) {
    storageSet(STORAGE_KEYS.selectedLocation, value);
  } else {
    storageRemove(STORAGE_KEYS.selectedLocation);
  }
};
